import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import MomentPropTypes from "react-moment-proptypes"
import { Column } from "react-virtualized"
import CSVButton from "@swipesense/csv-button"
import DataTable from "@swipesense/data-table"
import RangeFilter from "@swipesense/range-filter"
import Loading from "../Loading"
import PageBanner from "../PageBanner"
import Search from "../Search"
import moment from "moment"
import { basicSort } from "../../utils/sort"
import DeleteModal from "../DeleteModal"
import { subjectsPropTypes } from "../../utils/propTypes"
import "./CaseList.scss"

const dateFormat = "MMMM D, YYYY"

const CaseList = (props) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [caseFilter, setCaseFilter] = useState("allCases")
  const [sortBy, setSortBy] = useState("startAt")
  const [sortDirection, setSortDirection] = useState("DESC")
  const [rowClickedProps, setRowClickedProps] = useState({})
  const [archivingRows, setArchivingRows] = useState([])

  useEffect(() => {
    setArchivingRows([])
  }, [props.caseType])

  const clickRow = ({ rowData }) => {
    setRowClickedProps({
      facilityId: props.facilityId,
      caseId: rowData.internalId,
    })
  }

  const handleSortChange = (sortBy, sortDirection) => {
    setSortBy(sortBy)
    setSortDirection(sortDirection)
  }

  const handleSearchTerm = (e) => {
    setSearchTerm(e.target.value)
  }

  const titleCaseType = (type) => {
    if (type === "patientPositive") {
      return "Patient Positive"
    } else {
      return "Employee Positive"
    }
  }

  const rowHighlighter = (row) => {
    const kase = presentationData[row.index]
    return kase?.disabled ? "case-row disabled" : "case-row"
  }

  const filterData = (data) => {
    let caseNumberResults = []
    let subjectResults = []

    const filteredData =
      caseFilter === "allCases"
        ? data
        : data.filter((row) => row.type === caseFilter)

    if (searchTerm === "") {
      return filteredData
    }

    caseNumberResults = filteredData.filter((result) => {
      return result.internalId.toString().indexOf(searchTerm) !== -1
    })

    subjectResults = filteredData.filter((result) => {
      if (result.locations) {
        return result.locations.some(
          (loc) =>
            loc.name
              .toString()
              .toLowerCase()
              .indexOf(searchTerm.toLowerCase()) !== -1
        )
      } else {
        return (
          result.user.name
            .toString()
            .toLowerCase()
            .indexOf(searchTerm.toLowerCase()) !== -1
        )
      }
    })
    // Create Set to remove duplicates
    const combinedResultSet = new Set(caseNumberResults.concat(subjectResults))
    return Array.from(combinedResultSet)
  }

  const csvHeaders = [
    { label: "Case Number", key: "internalId" },
    { label: "Case Type", key: "type" },
    { label: "Subjects", key: "entity" },
    { label: "Case Start", key: "startAt" },
    { label: "Case End", key: "endAt" },
    { label: "Date Modified", key: "updatedAt" },
  ]

  const csvFilter = (row) => {
    return {
      internalId: row.internalId,
      type: titleCaseType(row.type),
      entity: row.entity,
      startAt: row.startAt,
      endAt: row.endAt,
      updatedAt: row.updatedAt,
    }
  }

  const changeFilter = (key) => {
    setCaseFilter(key)
  }

  const presentationMap = (data) => {
    return data.map((row) => ({
      ...row,
      type: row.type,
      entity: row.locations
        ? row.locations.map((loc) => loc.name).join(", ")
        : row.user.name,
      subjects: row.subjects,
      startAt: row.startAt.format(dateFormat),
      endAt: row.endAt.format(dateFormat),
      actions: row.internalId,
      disabled: archivingRows.includes(row.id),
      updatedAt: row.updatedAt.format(dateFormat),
    }))
  }

  const filteredData = filterData(props.cases)
  const sortedData = filteredData.sort(basicSort(sortBy, sortDirection))
  const presentationData = presentationMap(sortedData)
  const csvData = presentationData.map(csvFilter)
  const headerText =
    props.caseType === "archived" ? "Archived Cases" : "Open Cases"
  const csvFileName = `${props.facilityName
    ?.replace(" ", "_")
    .toLowerCase()}_${moment().local().format("DD_MM_YYYY")}_${
    props.caseType
  }_cases`

  return (
    <div className="CaseList">
      <PageBanner children={<h1>{headerText}</h1>} />
      <div className="filter-bar">
        <RangeFilter
          handleChange={changeFilter}
          options={[
            {
              key: "allCases",
              selected: caseFilter === "allCases",
              text: "All Cases",
            },
            {
              key: "patientPositive",
              selected: caseFilter === "patientPositive",
              text: "Patient Positive Cases",
            },
            {
              key: "employeePositive",
              selected: caseFilter === "employeePositive",
              text: "Employee Positive Cases",
            },
          ]}
        />
        <div className="search-and-download">
          <Search value={searchTerm} handleChange={handleSearchTerm} />
          <CSVButton
            headers={csvHeaders}
            data={csvData}
            filename={csvFileName}
          />
        </div>
      </div>
      <div className="data-wrapper">
        <DataTable
          className="CaseListTable"
          data={presentationData}
          rowHeight={50}
          loading={props.loading}
          loadingComponent={<Loading />}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={handleSortChange}
          onRowClick={clickRow}
          noRowsMessage="No cases could be found."
          rowClassName={rowHighlighter}
        >
          <Column
            dataKey="internalId"
            label="CASE NUMBER"
            disableSort={true}
            width={5}
          />
          <Column
            dataKey="type"
            label="CASE TYPE"
            disableSort={true}
            width={10}
            cellRenderer={({ cellData }) => titleCaseType(cellData)}
          />
          <Column
            dataKey="entity"
            label="SUBJECT"
            disableSort={true}
            width={25}
          />
          <Column
            dataKey="startAt"
            label="CASE START"
            disableSort={false}
            width={20}
          />
          <Column
            dataKey="endAt"
            label="CASE END"
            disableSort={false}
            width={20}
          />
          <Column
            dataKey="updatedAt"
            label="DATE MODIFIED"
            disableSort={false}
            width={25}
          />
          <Column
            dataKey="actions"
            label="ACTIONS"
            disableSort={true}
            width={20}
            cellRenderer={({ rowData }) => (
              <div className="case-actions">
                <button
                  className="archive-button"
                  disabled={archivingRows.includes(rowData.id)}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (!archivingRows.includes(rowData.id)) {
                      setArchivingRows([...archivingRows, rowData.id])
                      props.onArchive({
                        id: rowData.id,
                        type: rowData.type,
                        facilityId: props.facilityId,
                        subjects: rowData.subjects,
                        archive: props.caseType !== "archived",
                        timeout: props.timeout,
                      })
                    }
                  }}
                >
                  {props.caseType !== "archived" ? "Archive" : "Re-Open"}
                </button>
                <div className="separator">|</div>
                <DeleteModal
                  onArchive={props.onArchive}
                  onDelete={props.onDelete}
                  archiveCase={props.caseType !== "archived"}
                  id={rowData.id}
                  type={rowData.type}
                  subjects={rowData.subjects}
                  facilityId={props.facilityId}
                  disabled={archivingRows.includes(rowData.id)}
                />
              </div>
            )}
          />
        </DataTable>
      </div>
      <div className="total-results">
        {props.cases && (
          <div>
            {props.cases.length}{" "}
            {props.cases.length === 1 ? "result" : "results"}
          </div>
        )}
      </div>
      {React.createElement(props.onRowClickComponent, rowClickedProps)}
    </div>
  )
}

CaseList.propTypes = {
  caseType: PropTypes.oneOf(["open", "archived"]).isRequired,
  loading: PropTypes.bool,
  facilityId: PropTypes.string,
  facilityName: PropTypes.string,
  cases: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      internalId: PropTypes.string,
      type: PropTypes.string,
      updatedAt: MomentPropTypes.momentObj.isRequired,
      locations: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
        })
      ),
      user: PropTypes.shape({
        name: PropTypes.string,
      }),
      subjects: subjectsPropTypes,
      startAt: MomentPropTypes.momentObj,
      endAt: MomentPropTypes.momentObj,
    })
  ),
  onArchive: PropTypes.func,
  onDelete: PropTypes.func,
  onRowClickComponent: PropTypes.func.isRequired,
  timeout: PropTypes.number,
}

CaseList.defaultProps = {
  cases: [],
  timeout: 0,
}

export default CaseList
